import React from 'react';
import {
  ShieldCheck,
  Wind,
  Cpu,
  Wrench,
  BadgeCheck,
  Truck,
  Flag,
  Zap,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { MaskedLines, Reveal, EASE } from './reveal';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Galvanized Armor',
    body:
      'Every hammer is fully galvanized — nuts and bolts stay tighter longer and rust never gets a foothold. Runs harder, lasts longer.',
  },
  {
    icon: Wind,
    title: 'Pneumatic Lift & Drop',
    body:
      "Most hammers use air pressure only to LIFT the ram, then let gravity do the hammering. The Absolute Hammer uses air to both LIFT AND DROP the ram — every stroke is a full-power stroke.",
  },
  {
    icon: Cpu,
    title: 'Off-Hammer "Black Box"',
    body:
      'Proprietary controller lives off the hammer — no electronics beaten to death by the workload they command.',
  },
  {
    icon: Wrench,
    title: 'Effortless Maintenance',
    body:
      'Simple pneumatic mechanism. No hydraulic lines to spring. No sensitive electronics on the ram. Serviceable in the field.',
  },
  {
    icon: BadgeCheck,
    title: '1-Year Electronics Warranty',
    body:
      'Full one-year warranty on the controller and all electronic components. We stand behind what we build.',
  },
  {
    icon: Truck,
    title: 'Ships Nationwide',
    body:
      'Delivered anywhere in the Lower 48. Ready to run out of the crate — just connect your shop compressor.',
  },
  {
    icon: Flag,
    title: 'Made in the U.S.A.',
    body:
      'Designed and built in-house in Fort Myers, Florida. Not resold, not rebadged — this is our hammer.',
  },
  {
    icon: Zap,
    title: 'Ready to Run',
    body:
      'Works with any standard shop compressor at 100+ PSI. No hydraulic power pack, no proprietary fuel — just plug in air.',
  },
];

export default function HammerFeatures() {
  return (
    <section
      id="hammer-features"
      data-testid="hammer-features-section"
      className="relative bg-black border-t border-white/5 py-16 sm:py-20 lg:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <span className="h-px w-8 sm:w-10 bg-cyan-400" />
              <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
                Why the Absolute Hammer
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.02] tracking-tight">
            <MaskedLines
              delay={0.15}
              lines={[
                'Built by contractors',
                <span className="text-cyan-300">for contractors.</span>,
              ]}
            />
          </h2>
          <Reveal delay={0.3}>
            <p className="mt-5 text-zinc-400 text-sm sm:text-base md:text-lg leading-relaxed">
              We redesigned the pneumatic pile driver from scratch to fix
              every issue we had with previous hammers we used. It's simpler,
              tougher, and easier to service.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {FEATURES.map((f, idx) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: EASE, delay: idx * 0.07 }}
                className="panel rounded-xl p-5 sm:p-6 flex flex-col"
                data-testid={`hammer-feature-${idx}`}
              >
                <div className="w-11 h-11 rounded-md bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-white text-lg font-semibold leading-tight">
                  {f.title}
                </h3>
                <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
